/**
 * Hale & Hound redesign behaviour (hh-rd-* sections + cart popup extras).
 * Everything here is progressive enhancement: each form still works as a
 * plain HTML form when this script fails to load.
 */
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

const routes = () => (window.Theme && window.Theme.routes) || {};

/* ---------- Add to cart (cards, upsells) ---------- */
async function addToCart(form) {
  const btn = form.querySelector('[type="submit"]');
  if (!btn || btn.getAttribute('aria-busy') === 'true') return;
  const label = btn.innerHTML;
  btn.setAttribute('aria-busy', 'true');
  btn.disabled = true;

  const formData = new FormData(form);
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) {
    formData.append('sections', sectionIds.join(','));
    formData.append('sections_url', window.location.pathname);
  }

  const quantity = Number(formData.get('quantity')) || 1;
  const deferred = CartLinesUpdateEvent.createPromise();
  form.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(formData.get('id')), quantity }],
      promise: deferred.promise,
    })
  );

  try {
    const res = await fetch(`${routes().cart_add_url || '/cart/add'}.js`, {
      method: 'POST',
      headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: formData,
    });
    const data = await res.json();
    const cartRes = await fetch(`${routes().cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } });
    const cart = await cartRes.json();

    if (data.status) {
      form.dispatchEvent(new CartErrorEvent({ error: data.description || data.message || 'Add to cart failed', code: 'INVALID' }));
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { didError: true, items: cart.items } });
      showError(form, data.description || data.message);
      btn.innerHTML = label;
    } else {
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { items: cart.items, itemCount: quantity, sections: data.sections, source: 'hh-rd', didError: false },
      });
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        if (!drawer.isOpen) drawer.open();
      } else if (!document.querySelector('cart-items-component')) {
        window.location.href = routes().cart_url || '/cart';
        return;
      } else if (document.body.dataset.template === 'cart' || window.location.pathname.endsWith('/cart')) {
        window.location.reload();
        return;
      }
      btn.textContent = 'Added';
      btn.classList.add('is-done');
      setTimeout(() => { btn.innerHTML = label; btn.classList.remove('is-done'); }, 2200);
    }
  } catch (err) {
    deferred.reject(err);
    form.submit();
    return;
  } finally {
    btn.removeAttribute('aria-busy');
    btn.disabled = false;
  }
}

function showError(form, message) {
  let el = form.parentElement.querySelector('.r-pc__err');
  if (!el) {
    el = document.createElement('p');
    el.className = 'r-pc__err';
    el.setAttribute('role', 'alert');
    form.after(el);
  }
  el.textContent = message || 'Sorry, that could not be added.';
  setTimeout(() => el.remove(), 5000);
}

document.addEventListener('submit', (e) => {
  const form = e.target;
  if (!(form instanceof HTMLFormElement)) return;

  if (form.matches('[data-hh-add]')) {
    e.preventDefault();
    addToCart(form);
    return;
  }

  if (form.matches('[data-hh-discount]')) {
    e.preventDefault();
    const code = (form.querySelector('input')?.value || '').trim();
    if (!code) return;
    window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(routes().cart_url || '/cart')}`;
    return;
  }

  if (form.matches('[data-hh-track-number]')) {
    e.preventDefault();
    const num = (form.querySelector('input')?.value || '').trim();
    if (!num) return;
    const tpl = form.dataset.hhTrackNumber || 'https://t.17track.net/en#nums={number}';
    window.open(tpl.replace('{number}', encodeURIComponent(num)), '_blank', 'noopener');
  }
});

/* ---------- Collection filters + sort ---------- */
document.addEventListener('change', (e) => {
  const el = e.target;
  if (!(el instanceof HTMLElement)) return;
  if (el.matches('[data-hh-autosubmit]')) {
    const form = el.form || el.closest('form');
    if (!form) return;
    // On small screens the filter panel has an explicit Apply button.
    if (el.closest('.r-side') && window.matchMedia('(max-width: 900px)').matches) return;
    form.requestSubmit ? form.requestSubmit() : form.submit();
  }
});

document.addEventListener('click', (e) => {
  const t = e.target instanceof Element ? e.target : null;
  if (!t) return;
  const open = t.closest('[data-hh-filter-open]');
  const close = t.closest('[data-hh-filter-close]');
  if (open || close) {
    const panel = document.getElementById((open || close).getAttribute('aria-controls'));
    if (!panel) return;
    const isOpen = !!open;
    panel.classList.toggle('is-open', isOpen);
    document.documentElement.style.overflow = isOpen ? 'hidden' : '';
    document.querySelectorAll(`[data-hh-filter-open][aria-controls="${panel.id}"]`).forEach((b) => b.setAttribute('aria-expanded', String(isOpen)));
    if (isOpen) panel.querySelector('[data-hh-filter-close]')?.focus();
    else document.querySelector(`[data-hh-filter-open][aria-controls="${panel.id}"]`)?.focus();
  }
});

document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return;
  const panel = document.querySelector('.r-side.is-open');
  if (panel) panel.querySelector('[data-hh-filter-close]')?.click();
});

/* ---------- Policy page: highlight the section in view ---------- */
function initToc() {
  const toc = document.querySelector('[data-hh-toc]');
  if (!toc || !('IntersectionObserver' in window)) return;
  const links = [...toc.querySelectorAll('a[href^="#"]')];
  const map = new Map(links.map((a) => [a.getAttribute('href').slice(1), a]));
  const io = new IntersectionObserver(
    (entries) => {
      entries.forEach((en) => {
        if (!en.isIntersecting) return;
        links.forEach((l) => l.classList.remove('is-on'));
        map.get(en.target.id)?.classList.add('is-on');
      });
    },
    { rootMargin: '-20% 0px -70% 0px' }
  );
  map.forEach((_, id) => { const s = document.getElementById(id); if (s) io.observe(s); });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initToc, { once: true });
else initToc();

/* ---------- Cart popup title: "Added to cart" after an add ---------- */
import('@shopify/events').then(({ StandardEvents }) => {
  document.addEventListener(StandardEvents.cartLinesUpdate, (e) => {
    if (e.action !== 'add') return;
    const drawer = document.querySelector('theme-drawer#cart-drawer');
    if (!drawer) return;
    drawer.classList.add('hhr-just-added');
    const dialog = drawer.querySelector('dialog');
    dialog?.addEventListener('close', () => drawer.classList.remove('hhr-just-added'), { once: true });
  });
});
